import { useContext, useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isNil } from 'ramda';

import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { requestContingencyLineItemsList } from 'src/store/change_orders/change_orders.action';
import { getChangeOrderCurrentPeriod, getTradeContractId } from 'src/store/change_orders/change_orders.getters';
import { getCurrentSubprojectCurrencySymbol } from 'src/store/subProject/subProject.getters';
import { Result, RecordObject, ButtonBaseType, ContingencyLineItem } from 'src/models';
import { SearchableNestedDropdownInputProps } from 'src/components/InputRenderers/SearchableNestedDropdown';

import { InlineInput } from 'src/components/Inputs';
import { SearchableNestedDropdown as SearchableNestedDropdownComponent } from 'src/components/InputRenderers';

export function NestedContingencyDropdown() {
  const {
    value,
    onChange,
    classNames,
    onDiscard = mockFn,
    isDisabled,
    isReadonly,
    isAutoFocus,
    onClick,
  } = useContext(InputContext);

  const [contingencyLineItemsList, setContingencyLineItemsList] = useState<ContingencyLineItem[]>([]);

  const dispatch = useDispatch();

  const tradeId = useSelector(getTradeContractId);

  const currencySymbol = useSelector(getCurrentSubprojectCurrencySymbol);

  const currentPeriod = useSelector(getChangeOrderCurrentPeriod);

  const items = useMemo<ButtonBaseType[]>(() => {
    const lineItems: ButtonBaseType[] = [];

    const checkingArray: Array<string> = [];

    contingencyLineItemsList.forEach((item: ContingencyLineItem) => {
      if (!checkingArray.includes(item.group_id)) {
        checkingArray.push(item.group_id);
        lineItems.push({ label: item.group, id: item.group_id });
      }
    });

    return lineItems;
  }, [contingencyLineItemsList]);

  const nestedItems = useMemo<RecordObject<ButtonBaseType[]>>(() => {
    const nestedItemsObj: RecordObject<ButtonBaseType[]> = {};

    for (const item of items) {
      const values = contingencyLineItemsList.filter((line: ContingencyLineItem) => line.group_id === item.id);

      nestedItemsObj[item.id] = values.map((el: ContingencyLineItem) => {
        return { ...el, label: `${el.description} (${currencySymbol + el.amount})`, sub_label: `${el.group} | ` };
      });
    }

    return nestedItemsObj;
  }, [contingencyLineItemsList, items, currencySymbol]);

  useEffect(() => {
    if (tradeId && currentPeriod)
      dispatch(requestContingencyLineItemsList(tradeId, currentPeriod, { callback: handleResult }));
  }, [tradeId, dispatch, currentPeriod]);

  function handleResult(result: Result) {
    if (result.isOK) {
      setContingencyLineItemsList(result.payload);
    }
  }

  function handleOnFocus(callback: () => void) {
    return function call(e: React.FocusEvent) {
      e.preventDefault();

      callback();
    };
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnDiscard(fn: () => void) {
    return function call() {
      fn();

      if (onDiscard) onDiscard();
    };
  }

  function handleOnResetValue(fn: () => void) {
    return function call() {
      fn();

      if (onChange && !isNil(value)) onChange(null, true);

      if (onDiscard) onDiscard();
    };
  }

  function renderBaseComponent({ onClick, onFocus, onBlur, onDiscard, ...props }: SearchableNestedDropdownInputProps) {
    return (
      <InlineInput
        className={classNames?.input}
        value={props.value}
        onFocus={handleOnFocus(onFocus)}
        onClick={getHandleOnClick(onClick)}
        onBlur={onBlur}
        isDisabled={isDisabled || !items.length}
        autoFocus={isAutoFocus}
        onDiscard={handleOnDiscard(onDiscard)}
        onSubmit={handleOnDiscard(onDiscard)}
        onIconSubmit={handleOnResetValue(onDiscard)}
        isReadonly={isReadonly}
        iconId='trash'
      />
    );
  }

  return (
    <InputContext.Provider
      value={{
        classNames,
        value,
        onChange,
        isDisabled,
        isAutoFocus,
        isReadonly,
        items,
        nestedItems,
      }}
    >
      <SearchableNestedDropdownComponent renderInput={renderBaseComponent} isDropdownAutoOpen={true} />
    </InputContext.Provider>
  );
}
